import { makeStyles, mergeClasses, tokens, MessageBar, MessageBarBody } from '@coreai/fluentui-react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { getIsFreeProject, type AutomationProject } from '@/types/automation';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useAppStore } from '@/stores/appStore';
import { paths } from '@/routes';
import { SETTINGS_TAB } from '@/routes/queryParams';

const useStyles = makeStyles({
  banner: {
    flexShrink: 0,
    marginTop: tokens.spacingVerticalL,
    borderRadius: tokens.borderRadiusMedium,
  },
  link: {
    color: tokens.colorBrandForegroundLink,
    ':hover': {
      color: tokens.colorBrandForegroundLinkHover,
    },
  },
});

export interface FreeGrantBannerProps {
  /** The current project. The banner only renders for free-grant projects. */
  project: AutomationProject | null | undefined;
  /** Azure region for the project, used to build the Settings link. */
  locationName: string | undefined;
}

/**
 * Info banner shown on the project overview for free-grant projects. It points
 * the user at the Free grants tab under Settings (where the detailed meter
 * balances live) rather than rendering the balances inline. Renders nothing for
 * regular (paid) projects.
 */
export const FreeGrantBanner = ({ project, locationName }: FreeGrantBannerProps) => {
  const styles = useStyles();
  const { tenantId } = useParams<{ tenantId: string }>();
  const locationInStore = useAppStore((state) => state.selectedLocation);
  // Gated behind the same portal feature flag as the badge and Settings tab so
  // the whole free-grants surface can be rolled back / region-gated together.
  const freeGrantsEnabled = useFeatureFlag('freeGrants');

  if (!freeGrantsEnabled || !getIsFreeProject(project) || !project) {
    return null;
  }

  const loc = locationName ?? locationInStore;
  // Without a resolved region we can't build a valid Settings link; skip the
  // banner rather than render a dead link.
  if (!loc) {
    return null;
  }

  const settingsTabPath = paths.projectSettingsTab(loc, project.name, SETTINGS_TAB.freeGrants, tenantId);

  return (
    <MessageBar intent="info" className={styles.banner}>
      <MessageBarBody>
        This project has free grants available.{' '}
        <RouterLink className={mergeClasses(styles.link)} to={settingsTabPath}>
          View your free grant usage
        </RouterLink>
        .
      </MessageBarBody>
    </MessageBar>
  );
};
